/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { Layout, Typography, Image, Badge, Select, Button, Tooltip, Spin } from 'antd';
import './HeaderComponent.css';
import { MenuOutlined, ArrowLeftOutlined, ArrowRightOutlined, SyncOutlined, CloudOutlined, CloudSyncOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import useEventStore from '../../store/eventStore';
import { eventIdField, idField, pendingStatus } from '../../models/staticFields';
import useProductStore from '../../store/productStore';
import useCustomerStore from '../../store/customerStore';
import useProductTypeStore from '../../store/productTypeStore';
import useStandTypeStore from '../../store/standTypeStore';
import useStandStore from '../../store/standStore';
import useOrderStore from '../../store/orderStore';
import useOrderItemStore from '../../store/orderItemStore';
import useUserStore from '../../store/userStore';
import { useAuthStore } from '../../store/authUserStore';
import { UserRole } from '../../models/users';
import useCountryStore from '../../store/countryStore';
import { offlineStoreDb } from '../../offlinestore/offlineStoreDb';
import useStandSectorStore from '../../store/standSectoreStore';
import { useNavigate } from 'react-router-dom';
import { useSyncStatus } from '../../offlinestore/useOfflineSync';
import { markEventAsCached } from '../../offlinestore/syncQueue';
import { useTour } from '../Tour/useTour';

const { Header } = Layout;

interface HeaderComponentProps {
  toggleSidebar: () => void;
}

const HeaderComponent: React.FC<HeaderComponentProps> = ({ toggleSidebar }) => {
  const eventsStore = useEventStore();
  const usersStore = useUserStore();
  const standsStore = useStandStore();
  const standSectorStore = useStandSectorStore();
  const orderStore = useOrderStore();
  const orderItemsStore = useOrderItemStore();
  const productsStore = useProductStore();
  const customersStore = useCustomerStore();
  const productTypesStore = useProductTypeStore();
  const standTypesStore = useStandTypeStore();
  const countriesStore = useCountryStore();
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const [online, setOnline] = useState<boolean>(navigator.onLine);
  const { currentUser } = useAuthStore();
  const { t, i18n } = useTranslation();
  const { pendingCount, isSyncing, syncNow } = useSyncStatus();
  const { startTour } = useTour();


  const refreshOfflineDb = async () => {
    // cleanup
    const pendingEventsIds = (await eventsStore.fetchEventsByStatus(pendingStatus))?.map(pe => pe.id);
    if (pendingEventsIds?.length > 0) {
      await offlineStoreDb.events.where(idField).noneOf(pendingEventsIds).delete();
      await offlineStoreDb.eventProducts.where(eventIdField).noneOf(pendingEventsIds).delete();
      await offlineStoreDb.eventCustomers.where(eventIdField).noneOf(pendingEventsIds).delete();
      await offlineStoreDb.orderItems.where(eventIdField).noneOf(pendingEventsIds).delete();
      await offlineStoreDb.orders.where(eventIdField).noneOf(pendingEventsIds).delete();
      await offlineStoreDb.standSectors.where(eventIdField).noneOf(pendingEventsIds).delete();
      await offlineStoreDb.stands.where(eventIdField).noneOf(pendingEventsIds).delete();
      await offlineStoreDb.events.where(idField).noneOf(pendingEventsIds).delete();
    }

    // setup
    await productsStore.fetchItems();
    await customersStore.fetchItems();
    await usersStore.fetchItems();
    await productTypesStore.fetchItems();
    await standTypesStore.fetchItems();
    await countriesStore.fetchItems();
    
    const pendingEvents = await eventsStore.fetchEventsByStatus(pendingStatus);
    if (pendingEvents?.length > 0) {
      await Promise.all(pendingEvents.map(async pendingEvent => {
        await standsStore.fetchStandsByEventId(pendingEvent.id, false);
        await standSectorStore.fetchStandSectorsByEventId(pendingEvent.id, false);
        await orderStore.fetchOrdersByEventId(pendingEvent.id, false);
        await orderItemsStore.fetchOrderItemsByEventId(pendingEvent.id, false);
        await customersStore.fetchCustomersByEvent(pendingEvent.id, false);
        await productsStore.fetchProductsByEvent(pendingEvent.id, false);
        
        await markEventAsCached(pendingEvent.id, pendingEvent.name || pendingEvent.id, true);
      }));
    }
    console.log('[refreshOfflineDb] Offline data refresh completed');
  }

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => {
      setOnline(false);
      navigate('/');
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (token && navigator.onLine && (currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.ACCOUNTANT)) {
      refreshOfflineDb();
    }
  }, [token]);

  const handleChangeLanguage = (value: string) => {
    i18n.changeLanguage(value);
  };

  return (
    <Header className="header">
      <Button
        className="menu-toggle"
        type="text"
        icon={<MenuOutlined />}
        onClick={toggleSidebar}
        style={{ marginLeft: '10px', color: 'white', zIndex: 1100 }}
        aria-label="Toggle Sidebar"
      />
      <Button
        className="back-button"
        type="text"
        shape="circle"
        icon={<ArrowLeftOutlined style={{ fontSize: 20 }} />}
        onClick={() => navigate(-1)}
        style={{
          marginLeft: '10px',
          color: 'white',
          background: 'rgba(255,255,255,0.08)',
          border: 'none',
          zIndex: 1100,
        }}
        aria-label="Back"
      />
      <Button
        className="forward-button"
        type="text"
        shape="circle"
        icon={<ArrowRightOutlined style={{ fontSize: 20 }} />}
        onClick={() => navigate(1)}
        style={{
          marginLeft: '6px',
          color: 'white',
          background: 'rgba(255,255,255,0.08)',
          border: 'none',
          zIndex: 1100,
        }}
        aria-label="Forward"
      />
      <div className="logo">
        <Image src="/logo.png" preview={false} style={{ margin: '10px', height: '40px' }} />
      </div>
      <div className="status-indicator">
        {(currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.ACCOUNTANT) && (
          <>
            <Badge
              status={online ? 'success' : 'error'}
              text={online ? t('general.online') : t('general.offline')}
              style={{ marginLeft: 'auto', lineHeight: '40px', color: 'white' }}
            />
            {pendingCount > 0 && (
              <Tooltip title={`${pendingCount} ${t('general.pendingSync')}`}>
                <Badge 
                  count={pendingCount} 
                  style={{ marginLeft: '10px', backgroundColor: isSyncing ? '#1890ff' : '#faad14' }}
                >
                  {isSyncing ? (
                    <SyncOutlined spin style={{ color: 'white', fontSize: '16px', marginLeft: '8px' }} />
                  ) : (
                    <CloudSyncOutlined style={{ color: 'white', fontSize: '16px', marginLeft: '8px' }} />
                  )}
                </Badge>
              </Tooltip>
            )}
            {pendingCount === 0 && online && (
              <Tooltip title={t('general.syncComplete')}>
                <CloudOutlined style={{ color: '#52c41a', fontSize: '16px', marginLeft: '10px' }} />
              </Tooltip>
            )}
          </>
        )}
      </div>
      <Tooltip title={t('tour.startTour')}>
        <Button
          type="text"
          shape="circle"
          icon={<QuestionCircleOutlined style={{ fontSize: 20 }} />}
          onClick={startTour}
          style={{
            marginLeft: '8px',
            color: 'white',
            background: 'rgba(255,255,255,0.08)',
            border: 'none',
          }}
          aria-label="Start Tutorial"
        />
      </Tooltip>
      <Select
        className="select-language"
        style={{
          width: 50,
          background: 'transparent',
          border: 'none',
        }}
        dropdownStyle={{ padding: 0, background: 'transparent' }}
        value={i18n.language}
        onChange={handleChangeLanguage}
        suffixIcon={null}
        options={[
          {
            value: 'de',
            label: (
              <Image
                style={{ border: '1px solid gray' }}
                src="/de.png"
                preview={false}
                alt="German"
                width={25}
              />
            )
          },
          {
            value: 'en',
            label: (
              <Image
                style={{ border: '1px solid gray' }}
                src="/gb.png"
                preview={false}
                alt="English"
                width={25}
              />
            )
          }
        ]}
        variant="filled"
      />
    </Header>
  );
};

export default HeaderComponent;
